import React, { useEffect, useState, useRef } from 'react';
import "react-image-gallery/styles/image-gallery.css";
import axios from 'axios';
import {
    CircularProgress,
    Button,
    TextField,
    Box,
    Fade,
    Alert,
    Collapse,
    Stepper,
    Step,
    StepLabel,
    Stack,
    IconButton,
    Checkbox,
    FormControlLabel,
    Typography,
    Card,
    CardContent,
    Drawer
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { LoadingCircle, EmailConfirmStep, PDFDownloadButton, WorkshopSelector, InfoPage } from './';

const BACKEND_URL = process.env.REACT_APP_BACKEND;

const favourites = () => {
    try {
        return JSON.parse(localStorage.getItem('favourites')) ?? [];
    } catch {
        return [];
    }
};

const steps = ['Sinu andmed', 'Ülevaade', 'Emaili kinnitus'];

const sortByName = (a, b) => {
    const nameA = `${a.name ?? ''} ${a.name ?? ''}`.toLowerCase();
    const nameB = `${b.name ?? ''} ${b.name ?? ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
};

export default function TeacherRegistration({ workshop, onSubmit }) {
    const [workshops, setWorkshops] = useState([]);
    const [favouriteWorkshops, setFavouriteWorkshops] = useState(favourites());
    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const [userInfo, setUserInfo] = useState({ first_name: '', last_name: '', email: '', school: '', morning_workshop: '', evening_workshop: '' });

    const [firstNameValid, setFirstNameValid] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [firstNameFocus, setFirstNameFocus] = useState(false);

    const [lastNameValid, setLastNameValid] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [lastNameFocus, setLastNameFocus] = useState(false);

    const [emailValid, setEmailValid] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [schoolValid, setSchoolValid] = useState(false);
    const [schoolError, setSchoolError] = useState(false);
    const [schoolFocus, setSchoolFocus] = useState(false);

    const [TTGStudent, setTTGStudent] = useState(false);

    const [firstWorkshopValid, setFirstWorkshopValid] = useState(false);
    const [firstWorkshopError, setFirstWorkshopError] = useState(false);
    const [firstWorkshopFocus, setFirstWorkshopFocus] = useState(false);

    const [secondWorkshopValid, setSecondWorkshopValid] = useState(false);
    const [secondWorkshopError, setSecondWorkshopError] = useState(false);
    const [secondWorkshopFocus, setSecondWorkshopFocus] = useState(false);

    const [canContinue, setCanContinue] = useState(false);

    const [emailToken, setEmailToken] = useState('');

    const [intervalId, setIntervalId] = useState(null);

    const [overbooked, setOverbooked] = useState(false);

    const [emailConfirmed, setEmailConfirmed] = useState(false);

    const [registrationStarted, setRegistrationStarted] = useState(false);
    const [registrationSucceeded, setRegistrationSucceeded] = useState(false);

    const [morningLocation, setMorningLocation] = useState(null);
    const [eveningLocation, setEveningLocation] = useState(null);

    const REFRESH_INTERVAL = 60000;

    const fetchData = async () => {
        try {
            const workshops_response = await axios.get(`${BACKEND_URL}/api/workshops?populate=*`);
            const workshop_data = workshops_response.data.data.sort(sortByName);

            const availability_response = await axios.get(`${BACKEND_URL}/api/workshops/available`);
            const availability_data = availability_response.data;

            let aggregate_data = workshop_data.map((workshop) => ({
                ...workshop,
                availableSpots: availability_data[workshop.documentId].sessions,
                sessionCount: availability_data[workshop.documentId].sessionCount
            }));

            aggregate_data.forEach((workshop, i) => {
                if (!workshop.availableSpots.morning) {
                    aggregate_data[i].availableSpots.morning = 0;
                }
                if (!workshop.availableSpots.evening) {
                    aggregate_data[i].availableSpots.evening = 0;
                }
            })

            setWorkshops(aggregate_data);
            setLoading(false);

            return aggregate_data;

        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    }

    const morningWorkshop = workshops.find(
        (workshop) => workshop.documentId === userInfo.morning_workshop
    );

    const eveningWorkshop = workshops.find(
        (workshop) => workshop.documentId === userInfo.evening_workshop
    );

    useEffect(() => {

        fetchData();

        setIntervalId(setInterval(fetchData, REFRESH_INTERVAL));

        return () => {
            clearInterval(intervalId);
        }

    }, []);

    useEffect(() => {
        if (userInfo.first_name !== '' && userInfo.first_name) {
            setFirstNameValid(true);
        } else {
            setFirstNameValid(false);
        }
    }, [userInfo.first_name, firstNameFocus]);

    useEffect(() => {
        if (userInfo.last_name !== '' && userInfo.last_name) {
            setLastNameValid(true);
        } else {
            setLastNameValid(false);
        }
    }, [userInfo.last_name, lastNameFocus]);

    useEffect(() => {
        if (isValidEmail()) {
            setEmailValid(true);
        } else {
            setEmailValid(false);
        }
    }, [userInfo.email, emailFocus]);

    useEffect(() => {
        if (userInfo.morning_workshop !== '' && userInfo.morning_workshop) {
            setFirstWorkshopValid(true);
        } else {
            setFirstWorkshopValid(false);
        }
    }, [userInfo.morning_workshop, firstWorkshopFocus]);

    useEffect(() => {
        if (userInfo.evening_workshop !== '' && userInfo.evening_workshop) {
            setSecondWorkshopValid(true);
        } else {
            setSecondWorkshopValid(false);
        }
    }, [userInfo.evening_workshop, secondWorkshopFocus]);

    useEffect(() => {
        if (userInfo.school !== '' && userInfo.school) {
            setSchoolValid(true);
        } else {
            setSchoolValid(false);
        }
    }, [userInfo, schoolFocus]);

    useEffect(() => {
        if (checkCanContinue()) {
            setCanContinue(true);
        } else {
            setCanContinue(false);
        };
    }, [userInfo, activeStep, emailValid, schoolValid, firstNameValid, lastNameValid, firstWorkshopValid, secondWorkshopValid, TTGStudent]);

    useEffect(() => {
        if (firstNameFocus) {
            setFirstNameError(false);
        }
    }, [firstNameFocus]);

    useEffect(() => {
        if (lastNameFocus) {
            setLastNameError(false);
        }
    }, [lastNameFocus]);

    useEffect(() => {
        if (emailFocus) {
            setEmailError(false);
        }
    }, [emailFocus]);

    useEffect(() => {
        if (schoolFocus) {
            setSchoolError(false);
        }
    }, [schoolFocus]);

    useEffect(() => {
        if (firstWorkshopFocus) {
            setFirstWorkshopError(false);
        }
    }, [firstWorkshopFocus]);

    useEffect(() => {
        if (secondWorkshopFocus) {
            setSecondWorkshopError(false);
        }
    }, [secondWorkshopFocus]);

    useEffect(() => {
        if (TTGStudent) {
            handleUserChange('school', 'Tartu Tamme Gümnaasium');
            setSchoolValid(true);
            setSchoolError(false);
        } else {
            handleUserChange('school', '');
            setSchoolValid(true);
        }
    }, [TTGStudent]);

    useEffect(() => {
        checkForOverbooking(workshops);
    }, [userInfo.morning_workshop, userInfo.evening_workshop, overbooked, activeStep]);

    const handleEmailConfirmed = (morningLocation, eveningLocation) => {
        setMorningLocation(morningLocation);
        setEveningLocation(eveningLocation);
        setEmailConfirmed(true);

    }

    const handleTTGStudentCheckedChange = () => {
        setTTGStudent(!TTGStudent);
    }

    const handleNext = () => {

        if (!canContinue) {

            if (!firstNameValid) {
                setFirstNameError(true);
            }

            if (!lastNameValid) {
                setLastNameError(true);
            }

            if (!emailValid) {
                setEmailError(true);
            }

            if (!schoolValid) {
                setSchoolError(true);
            }

            if (!firstWorkshopValid) {
                setFirstWorkshopError(true);
            }

            if (!secondWorkshopValid) {
                setSecondWorkshopError(true);
            }

            return;

        } else {
            if (activeStep === steps.length - 1) {
                handleRegistration();
            }
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleUserChange = (field, value) => {
        setUserInfo((prev) => ({ ...prev, [field]: value }));
    }

    const getWorkshopName = (workshopId) => {
        const workshop = workshops.find(ws => ws.documentId === workshopId);
        return workshop?.name ?? '-';
    }

    const isUserSessionAvailable = (workshopId, timeslot) => {
        const workshop = workshops.find(ws => ws.documentId === workshopId);
        const spotsFull = workshop.availableSpots[timeslot] < 1;
        let otherSessionSame = false;

        if (workshopId === userInfo[`${timeslot === 'morning' ? 'evening_workshop' : 'morning_workshop'}`]) {
            otherSessionSame = true;
        }

        return spotsFull || otherSessionSame;
    }

    const checkForOverbooking = (workshops) => {

        if (userInfo.morning_workshop && userInfo.morning_workshop !== '') {
            const morning_workshop = workshops.find(ws => ws.documentId === userInfo.morning_workshop);

            if (morning_workshop.availableSpots.morning < 1) {
                setOverbooked(true);
                setActiveStep(0);
                return true;
            }
        }

        if (userInfo.evening_workshop && userInfo.evening_workshop !== '') {
            const evening_workshop = workshops.find(ws => ws.documentId === userInfo.evening_workshop);

            if (evening_workshop.availableSpots.evening < 1) {
                setOverbooked(true);
                setActiveStep(0);
                return true;
            }
        }

        setOverbooked(false);
        return false;

    }

    const handleRegistration = async () => {

        if (registrationStarted) {
            return;
        }

        setRegistrationStarted(true);

        const updatedWorkshops = await fetchData();

        const isOverbooked = checkForOverbooking(updatedWorkshops);

        if (!isOverbooked) {
            const requestData = {
                parentBookingData: userInfo,
                childBookingData: []
            }

            try {
                const res = await axios.post(`${BACKEND_URL}/api/bookings/register`, requestData);
                if (res.status === 200) {
                    setEmailToken(res.data.parent.email_token);
                    handleNext();

                    setRegistrationStarted(false);
                    setRegistrationSucceeded(true);

                    clearInterval(intervalId);
                }
            } catch (err) {

                setRegistrationStarted(false);
                setRegistrationSucceeded(false);

                console.error(err);
            }
        } else {
            setRegistrationStarted(false);
            setRegistrationSucceeded(false);
        }
    }

    const isValidEmail = () => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const status = regex.test(userInfo.email);
        return status;
    }

    const canContinueFromUserInfo = () => {
        if (firstNameValid && lastNameValid && emailValid && schoolValid && firstWorkshopValid && secondWorkshopValid && !overbooked) {
            return true;
        } else {
            return false;
        }
    }

    const checkCanContinue = () => {
        if (activeStep === 0) {
            return canContinueFromUserInfo();
        } else {
            return true;
        }

    }

    const [infoWorkshop, setInfoWorkshop] = useState(null);
    const [open, setOpen] = useState(false);

    const handleOpenWorkshopInfo = (documentId) => {
        const workshop = workshops.find(
            (ws) => ws.documentId === documentId
        );

        if (!workshop) {
            console.error(
                'Could not find workshop:',
                documentId
            );
            return;
        }

        setInfoWorkshop(workshop);
        setOpen(true);
    };

    const handleToggleFavourite = (workshopId) => {
        setFavouriteWorkshops((prev) => {
            const next = prev.includes(workshopId)
                ? prev.filter((id) => id !== workshopId)
                : [...prev, workshopId];

            localStorage.setItem('favourites', JSON.stringify(next));

            return next;
        });
    };


    const stepContent = [
        // First step - user info
        <Box display="flex" flexDirection="column" gap={2}>
            <TextField
                required
                label="Eesnimi"
                value={userInfo.first_name}
                error={firstNameError}
                onFocus={() => setFirstNameFocus(true)}
                onBlur={() => setFirstNameFocus(false)}
                onChange={(e) => handleUserChange("first_name", e.target.value)}
            />
            <TextField
                required
                label="Perenimi"
                value={userInfo.last_name}
                error={lastNameError}
                onFocus={() => setLastNameFocus(true)}
                onBlur={() => setLastNameFocus(false)}
                onChange={(e) => handleUserChange("last_name", e.target.value)}
            />
            <TextField
                required
                label="Email"
                value={userInfo.email}
                error={emailError}
                onFocus={() => setEmailFocus(true)}
                onBlur={() => setEmailFocus(false)}
                onChange={(e) => handleUserChange("email", e.target.value)}
            />
            <TextField
                required
                label="Kool"
                value={userInfo.school}
                error={schoolError}
                disabled={TTGStudent}
                onFocus={() => setSchoolFocus(true)}
                onBlur={() => setSchoolFocus(false)}
                onChange={(e) => handleUserChange("school", e.target.value)}
            />
            <FormControlLabel control={<Checkbox checked={TTGStudent} onChange={handleTTGStudentCheckedChange} />} label="Olen Tartu Tamme Gümnaasiumi õpilane" />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mt: 2, }} >
                <WorkshopSelector
                    title="Hommikune töötuba"
                    timeslot="morning"
                    workshops={workshops}
                    selectedWorkshop={userInfo.morning_workshop}
                    favourites={favouriteWorkshops}
                    onSelect={(workshopId) => handleUserChange('morning_workshop', workshopId)}
                    onToggleFavourite={handleToggleFavourite}
                    onOpenInfo={handleOpenWorkshopInfo}
                    isWorkshopUnavailable={isUserSessionAvailable} />
                <WorkshopSelector
                    title="Õhtune töötuba"
                    timeslot="evening" workshops={workshops}
                    selectedWorkshop={userInfo.evening_workshop}
                    favourites={favouriteWorkshops}
                    onSelect={(workshopId) => handleUserChange('evening_workshop', workshopId)}
                    onToggleFavourite={handleToggleFavourite}
                    onOpenInfo={handleOpenWorkshopInfo}
                    isWorkshopUnavailable={isUserSessionAvailable} />
            </Box>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
            >
                <Box
                    sx={{
                        height: '80vh',
                        width: {
                            xs: '90vw',
                            md: '80vw',
                        },
                        margin: '0 auto',
                        overflowY: 'auto',
                    }}
                >
                    {infoWorkshop && (
                        <InfoPage
                            workshop={infoWorkshop} onClose={() => setOpen(false)} onToggleFavourite={handleToggleFavourite} favourite={favouriteWorkshops.includes(infoWorkshop.documentId)}
                        />
                    )}
                </Box>
            </Drawer>
        </Box>,

        // Second step - confirmation

        <Box
            sx={{
                width: '100%',
                maxWidth: 900,
                mx: 'auto',
                px: { xs: 1, sm: 2 },
                py: { xs: 2, sm: 3 },
            }}
        >
            <Stack spacing={3}>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={300} mb={1}>
                        Kontrolli oma registreering üle
                    </Typography>

                    <Typography variant="body1" color="text.secondary">
                        Veendu, et sinu andmed ja valitud töötoad on õiged.
                    </Typography>
                </Box>

                {/* User */}
                <Card
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                    }}
                >
                    <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                            <PersonOutlineOutlinedIcon color="primary" />

                            <Typography variant="h6" fontWeight={600}>
                                Registreeruja
                            </Typography>
                        </Stack>

                        <Typography variant="h5" fontWeight={500}>
                            {userInfo.first_name} {userInfo.last_name}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Workshops */}
                <Box>
                    <Typography variant="h6" fontWeight={600} mb={1.5}>
                        Valitud töötoad
                    </Typography>

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                    >

                        {/* Morning */}
                        <Card
                            elevation={0}
                            onClick={() => setInfoWorkshop(morningWorkshop)}
                            sx={{
                                flex: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',

                                '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 2,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    mb={2}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'warning.50',
                                        }}
                                    >
                                        <WbSunnyOutlinedIcon color="warning" />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Hommikune töötuba
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            10.00–12.00
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography
                                    variant="h6"
                                    fontWeight={500}
                                    lineHeight={1.35}
                                >
                                    {morningWorkshop?.name}
                                </Typography>

                                <Button
                                    variant="text"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenWorkshopInfo(morningWorkshop.documentId);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        alignSelf: 'flex-start',
                                        px: 0,
                                    }}
                                >
                                    Vaata töötoa kirjeldust
                                </Button>
                            </CardContent>
                        </Card>


                        {/* Evening */}
                        <Card
                            elevation={0}
                            onClick={() => setInfoWorkshop(eveningWorkshop)}
                            sx={{
                                flex: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',

                                '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 2,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    mb={2}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'primary.50',
                                        }}
                                    >
                                        <DarkModeOutlinedIcon color="primary" />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Õhtune töötuba
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            13.00–14.30
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography
                                    variant="h6"
                                    fontWeight={500}
                                    lineHeight={1.35}
                                >
                                    {eveningWorkshop?.name}
                                </Typography>

                                <Button
                                    variant="text"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenWorkshopInfo(eveningWorkshop.documentId);;
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        alignSelf: 'flex-start',
                                        px: 0,
                                    }}
                                >
                                    Vaata töötoa kirjeldust
                                </Button>
                            </CardContent>
                        </Card>

                    </Stack>
                </Box>

            </Stack>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
            >
                <Box
                    sx={{
                        height: '80vh',
                        width: {
                            xs: '90vw',
                            md: '80vw',
                        },
                        margin: '0 auto',
                        overflowY: 'auto',
                    }}
                >
                    {infoWorkshop && (
                        <InfoPage
                            workshop={infoWorkshop} onClose={() => setOpen(false)} onToggleFavourite={handleToggleFavourite} favourite={favouriteWorkshops.includes(infoWorkshop.documentId)}
                        />
                    )}
                </Box>
            </Drawer>
        </Box>,


        // Third step - waiting for email confirmation
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <EmailConfirmStep email_token={emailToken} onSuccess={handleEmailConfirmed} />
            <Collapse in={emailConfirmed}>
                <PDFDownloadButton workshops={workshops} userInfo={userInfo} plannedBookings={[]} morningLocation={morningLocation} eveningLocation={eveningLocation} />
            </Collapse>
        </Box>

    ]

    if (loading) return <LoadingCircle />

    return (
        <Box sx={{ maxWidth: { xl: '65%', md: '95%' }, margin: '2vh auto 10vh auto' }}>
            <Fade in={overbooked} timeout={600}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300, // higher than most MUI elements
                        width: 'auto',
                        maxWidth: '90%',
                    }}
                >
                    <Alert severity="error" variant="filled">
                        Mõned sinu broneeritud töötoad on vahepeal täitunud. Palun kustuta punaselt märgitud registreeringud ja loo need uuesti.
                    </Alert>
                </Box>
            </Fade>
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                <IconButton aria-label="go back" href='/register' size='large'>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Stepper activeStep={activeStep} sx={{ mb: 9, mt: 3 }}>
                {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    return (
                        <Step key={label} {...stepProps}>
                            <StepLabel {...labelProps}>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            {/* <Typography variant="h5"
                component="h2"
                sx={{
                    fontWeight: 200,
                    mb: 3,
                    textAlign: 'left',
                    color: 'text.primary',
                    lineHeight: 1.3,
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}>
                {steps[activeStep]}
            </Typography> */}
            {stepContent[activeStep]}
            {activeStep === steps.length ? (
                <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button onClick={handleReset}>Reset</Button>
                    </Box>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Button
                            color="inherit"
                            disabled={activeStep === 0 || activeStep === 2}
                            onClick={handleBack}
                            sx={{ mr: 1, opacity: (activeStep !== 0 && activeStep !== 2) ? 1 : 0 }}
                        >
                            Tagasi
                        </Button>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button
                            disabled={activeStep === 2}
                            sx={{ opacity: (activeStep !== 2) ? 1 : 0 }}
                            variant={activeStep === 1 ? 'contained' : 'text'}
                            onClick={activeStep === 1 ? handleRegistration : handleNext}>
                            {activeStep === steps.length - 2 ? 'Kinnita registreerimine' : 'Edasi'}
                        </Button>
                        <Fade
                            in={registrationStarted}
                            style={{
                                transitionDelay: loading ? '800ms' : '0ms',
                            }}
                            unmountOnExit
                        >
                            <CircularProgress />
                        </Fade>

                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
